/**
 * Browser / OS tray notifications via Service Worker.
 * Chrome/Edge suppress `new Notification()` while the tab is focused — SW path
 * is required for foreground toasts.
 */

import {
  getNotificationServiceWorkerRegistration,
  registerNotificationServiceWorker,
} from "./notificationServiceWorker";

export function isSecureNotificationContext() {
  return typeof window !== "undefined" && window.isSecureContext === true;
}

export function isBrowserNotificationSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    isSecureNotificationContext()
  );
}

export function getBrowserNotificationPermission() {
  if (!isBrowserNotificationSupported()) return "unsupported";
  return Notification.permission;
}

export function stripNotificationHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export async function requestBrowserNotificationPermission() {
  if (!isBrowserNotificationSupported()) return "unsupported";
  if (Notification.permission === "granted") {
    void registerNotificationServiceWorker();
    return "granted";
  }
  if (Notification.permission === "denied") return "denied";
  try {
    const result = await Notification.requestPermission();
    if (result === "granted") void registerNotificationServiceWorker();
    return result;
  } catch (_e) {
    return getBrowserNotificationPermission();
  }
}

function buildNotificationPayload(input = {}) {
  const title = String(
    input.title || input.notificationTitle || "FlexHR — Njoftim"
  ).trim();
  const rawBody =
    input.message ||
    input.body ||
    input.notificationBody ||
    input.text ||
    "";
  const body = stripNotificationHtml(rawBody) || "Keni një njoftim të ri.";
  const url = input.url || input.notificationUrl || "/kryefaqja";
  const tag = `flexhr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  return {
    title,
    body,
    url,
    tag,
    data: { url, ...(input.data || {}) },
  };
}

async function showViaServiceWorker(payload) {
  await registerNotificationServiceWorker();
  const reg = await getNotificationServiceWorkerRegistration();
  if (!reg?.showNotification) return null;

  // Minimal options — avoid icon URLs (fetch failures can abort the show).
  // requireInteraction helps macOS/Edge surface a banner instead of quiet NC.
  await reg.showNotification(payload.title, {
    body: payload.body,
    tag: payload.tag,
    renotify: true,
    requireInteraction: true,
    silent: false,
    data: payload.data,
  });

  return { ok: true, via: "service-worker", tag: payload.tag };
}

function showViaPageNotification(payload) {
  const n = new Notification(payload.title, {
    body: payload.body,
    tag: `${payload.tag}-page`,
    renotify: true,
    requireInteraction: false,
    silent: false,
    data: payload.data,
  });
  n.onclick = () => {
    try {
      window.focus();
      const path = n.data?.url || payload.url;
      if (path) {
        const target = path.startsWith("http")
          ? path
          : `${window.location.origin}${path.startsWith("/") ? "" : "/"}${path}`;
        window.location.assign(target);
      }
    } catch (_e) {
      /* ignore */
    }
    n.close();
  };
  setTimeout(() => {
    try {
      n.close();
    } catch (_e) {
      /* ignore */
    }
  }, 20000);
  return { ok: true, via: "page", notification: n };
}

/**
 * @returns {Promise<{ok:boolean, reason?:string, via?:string}>}
 */
export async function showBrowserNotification(input = {}) {
  if (!isBrowserNotificationSupported()) {
    return { ok: false, reason: "unsupported" };
  }
  if (Notification.permission !== "granted") {
    // Ask UI to open the enable modal (Header listens).
    try {
      window.dispatchEvent(
        new CustomEvent("flexhr:need-push-permission", {
          detail: { permission: Notification.permission },
        })
      );
    } catch (_e) {
      /* ignore */
    }
    return { ok: false, reason: Notification.permission || "not_granted" };
  }

  const payload = buildNotificationPayload(input);

  try {
    const viaSw = await showViaServiceWorker(payload);
    if (viaSw?.ok) return viaSw;
  } catch (err) {
    console.warn("[FlexHR push] SW failed", err);
  }

  try {
    return showViaPageNotification(payload);
  } catch (err) {
    console.warn("[FlexHR push] page Notification failed", err);
    return { ok: false, reason: "show_failed" };
  }
}

export function checkBrowserPushSubscription() {
  const supported = isBrowserNotificationSupported();
  const permission = getBrowserNotificationPermission();
  return {
    supported,
    permission,
    subscribed: supported && permission === "granted",
    blocked: permission === "denied",
    pending: permission === "default",
  };
}

export async function sendTestBrowserNotification() {
  const status = checkBrowserPushSubscription();
  if (!status.supported) return { ok: false, reason: "unsupported" };
  if (!status.subscribed) {
    return { ok: false, reason: status.blocked ? "denied" : "not_granted" };
  }
  const now = new Date();
  const time = now.toLocaleTimeString("sq-AL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return showBrowserNotification({
    title: "FlexHR — Njoftimi i testit",
    message: `Tray aktiv. ${time}`,
    url: "/kryefaqja",
    data: { test: true },
  });
}
